import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FiDollarSign, FiChevronDown, FiChevronRight } from "react-icons/fi";

import { ContentHeader, StatCard } from "../../../components/v4";
import { accounts } from "../../utils/accountConstants";
import api from "../../../utils/api";

/**
 * Format currency with no decimals
 */
const formatCurrency = (value) => {
	return Math.round(value).toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
};

/**
 * AccountGroupCard - Collapsible card for an account group
 */
const AccountGroupCard = ({ group, accounts: groupAccounts, total, defaultOpen = true }) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	return (
		<div className="v4-accounts__group">
			<button
				className="v4-accounts__group-header"
				onClick={() => setIsOpen(!isOpen)}
			>
				<span className="v4-accounts__group-title">{group}</span>
				<span className="v4-accounts__group-total">{formatCurrency(total)}</span>
				<span className="v4-accounts__group-chevron">
					{isOpen ? <FiChevronDown /> : <FiChevronRight />}
				</span>
			</button>
			{isOpen && (
				<div className="v4-accounts__group-content">
					{groupAccounts.map((account, idx) => (
						<div
							key={idx}
							className={`v4-accounts__row ${account.total < 0 ? "v4-accounts__row--negative" : ""} ${account.total === 0 ? "v4-accounts__row--zero" : ""}`}
						>
							<span className="v4-accounts__row-name">{account.name}</span>
							<span className="v4-accounts__row-value">{formatCurrency(account.total)}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

/**
 * Accounts - V4 Design Pattern
 *
 * Two-column layout (75/25):
 * - Left: Account groups in a 2-column grid
 * - Right: Sticky category summary panel
 */
const Accounts = () => {
	const [data, setData] = useState([]);
	const [accountDetails, setAccountDetails] = useState([]);
	const [manualGroups, setManualGroups] = useState([]);

	// Fetch account balance data
	useEffect(() => {
		const fetchMonthlyData = async () => {
			try {
				const res = await api.get(`/money/AccountBalance/`);
				setData(res.data);
			} catch (err) {
				console.log(err);
			}
		};
		fetchMonthlyData();
	}, []);

	// Process account data into groups
	useEffect(() => {
		const groupedAccounts = {};

		// Manual category groupings for the sidebar summary
		const manualGroupConfig = [
			{ name: "Cash", accounts: ["NL: Cash"] },
			{ name: "Checking", accounts: ["NL: Checking - D100", "Erin: PNC - Checking"] },
			{ name: "Business Checking", accounts: ["NL: Immerge Checking"] },
			{ name: "Savings", accounts: ["Erin: PNC - Savings", "NL: Immerge Saving", "NL: Home Saving - S000"] },
			{ name: "Credit Cards", accounts: ["NL: Chase - Sapphire Preferred", "NL: Chase - United Explorer", "NL: Chase - Freedom", "NL: Chase - Ink Bold Preferred", "NL: Chase - United Business", "Erin: Chase - Sapphire Preferred", "Erin: PNC - Points Credit Card", "Erin: PNC - CashBack Credit Card"] },
			{ name: "Credit Line", accounts: ["NL: Home - CL"] },
			{ name: "Tax Estimate", accounts: ["NL: Tax Estimate"] },
			{ name: "Investments", accounts: ["NL: COIN", "NL: VAN"] },
		];

		// Group accounts by their assigned group
		accounts.forEach((item) => {
			const group = item.group || "Ungrouped";
			if (!groupedAccounts[group]) {
				groupedAccounts[group] = {
					group: group,
					accounts: [],
					total: 0,
				};
			}

			// Get latest balance for this account
			const latestEntry = data
				.filter((d) => d.account === item.account)
				.sort((a, b) => new Date(b.date) - new Date(a.date))[0];

			item.total = latestEntry ? latestEntry.value : 0;

			groupedAccounts[group].accounts.push(item);
			groupedAccounts[group].total += item.total;
		});

		setAccountDetails(Object.values(groupedAccounts));

		// Build lookup for manual groups
		const accountToTotal = {};
		Object.values(groupedAccounts).forEach((grp) => {
			grp.accounts.forEach((acc) => {
				accountToTotal[acc.account] = acc.total || 0;
			});
		});

		// Compute manual category totals
		const manual = manualGroupConfig.map((grp) => {
			const subcategories = grp.accounts.map((accName) => ({
				account: accName,
				total: accountToTotal[accName] || 0,
			}));
			const total = subcategories.reduce((s, x) => s + x.total, 0);
			return {
				name: grp.name,
				subcategories,
				total,
			};
		});

		setManualGroups(manual);
	}, [data]);

	// Calculate key metrics
	const metrics = useMemo(() => {
		// Assets: Cash, Checking, Business Checking, Savings (positive balances)
		const assetCategories = ["Cash", "Checking", "Business Checking", "Savings", "Investments"];
		// Liabilities: Credit Cards, Credit Line, Tax Estimate (typically negative)
		const liabilityCategories = ["Credit Cards", "Credit Line", "Tax Estimate"];

		let totalAssets = 0;
		let totalLiabilities = 0;

		manualGroups.forEach((grp) => {
			if (assetCategories.includes(grp.name)) {
				totalAssets += grp.total;
			} else if (liabilityCategories.includes(grp.name)) {
				// Liabilities are stored as negative, so we add to get the absolute value
				totalLiabilities += Math.abs(grp.total);
			}
		});

		const netWorth = totalAssets - totalLiabilities;
		const grandTotal = manualGroups.reduce((s, x) => s + x.total, 0);

		return { totalAssets, totalLiabilities, netWorth, grandTotal };
	}, [manualGroups]);

	// Header actions
	const headerActions = (
		<>
			<Link to="/accountBalance" className="btn btn--secondary">All Balances</Link>
			<Link to="/addAccountBalance" className="btn btn--secondary">Add Single</Link>
			<Link to="/addAccountBalances/NL" className="btn btn--secondary">Add NL</Link>
			<Link to="/addAccountBalances/Erin" className="btn btn--secondary">Add Erin</Link>
		</>
	);

	return (
		<div className="v4-accounts">
			{/* Header */}
			<ContentHeader
				icon={<FiDollarSign />}
				title="Accounts"
				subtitle={`${accounts.length} accounts tracked`}
				metadata={formatCurrency(metrics.grandTotal)}
				actions={headerActions}
			/>

			{/* Stats Row */}
			<div className="v4-stats-row">
				<StatCard
					label="Total Assets"
					value={Math.round(metrics.totalAssets)}
					prefix="$"
					animate={true}
				/>
				<StatCard
					label="Total Liabilities"
					value={Math.round(metrics.totalLiabilities)}
					prefix="$"
					changeType="down"
					animate={true}
				/>
				<StatCard
					label="Net Worth"
					value={Math.round(metrics.netWorth)}
					prefix="$"
					changeType={metrics.netWorth >= 0 ? "up" : "down"}
					animate={true}
				/>
			</div>

			{/* 75/25 Layout */}
			<div className="v4-accounts__layout">
				{/* Main Content - Account Groups */}
				<div className="v4-accounts__main">
					<div className="v4-accounts__grid">
						{accountDetails.map((item, idx) => (
							<AccountGroupCard
								key={idx}
								group={item.group}
								accounts={item.accounts}
								total={item.total}
								defaultOpen={true}
							/>
						))}
					</div>
				</div>

				{/* Sidebar - Category Summary */}
				<aside className="v4-accounts__sidebar">
					<div className="v4-accounts__panel">
						<div className="v4-accounts__panel-header">
							<h3 className="v4-accounts__panel-title">Summary</h3>
						</div>
						<div className="v4-accounts__panel-content">
							{manualGroups.map((grp, idx) => (
								<div
									key={idx}
									className={`v4-accounts__summary-row ${grp.total < 0 ? "v4-accounts__summary-row--negative" : ""}`}
								>
									<span className="v4-accounts__summary-label">{grp.name}</span>
									<span className="v4-accounts__summary-value">{formatCurrency(grp.total)}</span>
								</div>
							))}
							<div className="v4-accounts__summary-total">
								<span className="v4-accounts__summary-label">Total</span>
								<span className="v4-accounts__summary-value">{formatCurrency(metrics.grandTotal)}</span>
							</div>
						</div>
					</div>
				</aside>
			</div>
		</div>
	);
};

export default Accounts;
